import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import {createRef, ref} from 'lit/directives/ref.js';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import styles from './x-installation-item.component.scss?as=stylesheet';
export class InstallationItem extends LitElement {

	static styles = [styles];

	// eslint-disable-next-line max-lines-per-function
	static get properties () {
		return {
			additionalText: {
				attribute: 'additional-text',
				type: String
			},
			code: {
				type: String
			},
			colorSwatch: {
				attribute: 'color-swatch',
				type: String
			},
			hasTerms: {
				attribute: 'has-terms',
				type: Boolean
			},
			image: {
				type: String
			},
			label: {
				type: String
			},
			multiSelect: {
				attribute: 'multi-select',
				type: Number
			},
			productCode: {
				attribute: 'product-code',
				type: String
			},
			productPrice: {
				attribute: 'product-price',
				type: Number
			},
			quantityValue: {
				attribute: 'quantity-value',
				type: Number
			},
			sectionCode: {
				attribute: 'section-code',
				type: String
			},
			sectionType: {
				attribute: 'section-type',
				type: String
			},
			selected: {
				type: Boolean
			},
			terms: {
				type: String
			},
			tooltipText: {
				attribute: 'tooltip-text',
				type: String
			}
		};
	}

	tooltipTrigger = createRef();

	quantityElement = createRef();

	constructor () {
		super();

		this.additionalText = '';
		this.code = '';
		this.hasTerms = 0;
		this.image = '';
		this.label = '';
		this.multiSelect = 0;
		this.outerOptionClasses = '';
		this.productCode = '';
		this.productPrice = '';
		this.productQuantity = 1;
		this.quantityValue = 0;
		this.sectionCode = '';
		this.sectionType = '';
		this.selected = false;
		this.terms = '';
		this.tooltipText = '';
	}

	firstUpdated () {
		// Tippy (Tooltips)
		if (this.tooltipTrigger?.value) {
			tippy(this.tooltipTrigger.value, {
				allowHTML: true,
				arrow: false,
				interactive: true,
				maxWidth: '290px',
				placement: 'bottom',
				zIndex: 2
			});
		}
	}

	renderNonImageSection () {
		const classes = {
			'x-install-option': true,
			'x-install-option--selected': this.selected,
			'x-install-option-non-image': true
		};

		return html`
			<button class="${classMap(classes)}" @click="${() => this._updateState()}">
				<div class="x-install-option__prompt">
					${this.label}
					${this.tooltipText ? html`<span ${ref(this.tooltipTrigger)} class="x-install-option__tooltip" data-tippy-content="${this.tooltipText}"></span>` : nothing}
				</div>
				${this.additionalText ? html`<div class="x-install-option__additional-text">${this.additionalText}</div>` : nothing}
			</button>
		`;
	}

	renderQuantitySection () {
		const classes = {
			'x-install-option': true,
			'x-install-option--selected': this.quantityValue > 0,
			'x-install-option-quantity': true
		};

		return html`
			<div class="${classMap(classes)}">
				<div class="x-install-option__prompt">
					${this.label}
					${this.tooltipText ? html`<span ${ref(this.tooltipTrigger)} class="x-install-option__tooltip" data-tippy-content="${this.tooltipText}"></span>` : nothing}
				</div>
				<div class="x-install-option-quantity__elements">
					<button class="x-install-option-quantity__icon x-install-option-quantity__icon--decrement" aria-label="Decrement Quantity" ?disabled="${this.quantityValue === 0}" @click="${this.decrementQuantity}"></button>
					<input type="number" class="x-install-option-quantity__input" .value="${this.quantityValue}" ${ref(this.quantityElement)} @change="${this.handleQuantityChange}" />
					<button class="x-install-option-quantity__icon x-install-option-quantity__icon--increment" aria-label="Increment Quantity" @click="${this.incrementQuantity}"></button>
				</div>
				${this.additionalText ? html`<div class="x-install-option__additional-text">${this.additionalText}</div>` : nothing}
			</div>
		`;
	}

	renderColorSwatchSection () {
		const classes = {
			'x-install-option': true,
			'x-install-option--selected': this.selected,
			'x-install-option-color-swatch': true
		};

		const swatchStyles = {
			'background-color': this.colorSwatch
		};

		return html`
			<button class="${classMap(classes)}" @click="${() => this._updateState()}">
				<span class="x-install-option-color-swatch__swatch" style="${styleMap(swatchStyles)}"></span>
				<div class="x-install-option__label">
					<div class="x-install-option__prompt">
						${this.label}
						${this.tooltipText ? html`<span ${ref(this.tooltipTrigger)} class="x-install-option__tooltip" data-tippy-content="${this.tooltipText}"></span>` : nothing}
					</div>
					${this.additionalText ? html`<div class="x-install-option__additional-text">${this.additionalText}</div>` : nothing}
				</div>
			</button>
		`;
	}

	renderDefaultSection () {
		const classes = {
			'x-install-option': true,
			'x-install-option--additional-text': Boolean(this.additionalText),
			'x-install-option--selected': this.selected,
			'x-install-option-with-image': true
		};

		return html`
			<button class="${classMap(classes)}" @click="${() => this._updateState()}">
				<div class="x-install-option-with-image__inner">
					<div class="x-install-option-with-image__image">
						<img src="${this.image}">
					</div>
					<div class="x-install-option__label">
						<div class="x-install-option__prompt">
							${this.label}
							${this.tooltipText ? html`<span ${ref(this.tooltipTrigger)} class="x-install-option__tooltip" data-tippy-content="${this.tooltipText}"></span>` : nothing}
						</div>
						${this.additionalText ? html`<div class="x-install-option__additional-text">${this.additionalText}</div>` : nothing}
					</div>
				</div>
			</button>
		`;
	}

	render () {
		switch (this.sectionType) {
			case 'option_without_image':
				return this.renderNonImageSection();
			case 'option_with_color':
				return this.renderColorSwatchSection();
			case 'option_with_quantity':
				return this.renderQuantitySection();
			case 'option_with_image':
			default:
				return this.renderDefaultSection();
		}
	}

	_updateState () {
		const options = {
			code: this.code,
			hasTerms: this.hasTerms,
			multiSelect: this.multiSelect,
			productCode: this.productCode,
			productPrice: this.productPrice,
			productQuantity: this.productQuantity,
			section: this.sectionCode,
			sectionType: this.sectionType,
			terms: this.terms,
			type: this.productCode ? 'product' : 'option'
		};

		this.dispatchEvent(new CustomEvent('option_click', {
			bubbles: true,
			composed: true,
			detail: options
		}));
	}

	decrementQuantity () {
		if (this.quantityValue === 0) {
			return;
		}

		this.quantityValue--;
		this.productQuantity = this.quantityValue;
		this._updateState();
	}

	incrementQuantity () {
		this.quantityValue++;
		this.productQuantity = this.quantityValue;
		this._updateState();
	}

	handleQuantityChange () {
		if (!this.quantityElement?.value) {
			return;
		}

		this.quantityValue = parseInt(this.quantityElement.value.value, 10);
		this.productQuantity = this.quantityValue;
		this._updateState();
	}
}

customElements.define('x-installation-item', InstallationItem);
